import { DOCUMENT, NgIf } from "@angular/common";
import { Component, Inject, OnInit, HostListener, OnDestroy, ViewChild } from "@angular/core";
import { AuthService } from "@auth0/auth0-angular";
import { NGXLogger } from "ngx-logger";
import { MessageService } from "primeng/api";
import {
  DialogService,
} from "primeng/dynamicdialog";
import { Router, NavigationEnd } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { environment } from "../../../environments/environment";
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { BadgeModule } from 'primeng/badge';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { StyleClassModule } from 'primeng/styleclass';
import { SupportService } from "../../services/support.service";
import { UserService } from "../../services/user.service";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { SupportModalComponent } from "../../pages/configurazione/faq/support-modal/support-modal.component";
import { FirebaseStorageService } from "../../services/firebase-storage.service";
import { saveAs } from 'file-saver';

interface Release {
  name: string;
  code: string;
}

@Component({
  selector: "nav-bar",
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    ButtonModule,
    MenuModule,
    BadgeModule,
    ChipModule,
    DialogModule,
    ToastModule,
    DynamicDialogModule,
    StyleClassModule,
    SupportModalComponent
  ],
  providers: [MessageService, DialogService],
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.css"],
})
export class NavBarComponent implements OnInit, OnDestroy {
  @ViewChild(SupportModalComponent) supportModal!: SupportModalComponent;
  displayInfoModal: boolean = false;
  showConfigPanel: boolean = false;
  hideMenu: boolean = false;
  hideConfigMenu: boolean = true;
  hideHelpMenu: boolean = true;
  hideComuniEUMenu: boolean = true;
  showBadge = true;
  isLoggedIn = false;
  isDownloading: boolean = false;
  public isAdmin:boolean = false;
  public isSuperAdmin:boolean = false;
  public isUtente:boolean = false;
  public isEnteEsecutore:boolean = false;
  protected navbarChip: string = "";
  protected hideChip:boolean = true;
  protected chipColor:String = '';
  private subscription = new Subscription();
  showSubMenu = false;

  protected appVersion:String=environment.version;
  public isTestEnvironment:boolean = false;
  public activeRoute: string = '';

  constructor(
    public auth: AuthService,
    private messageService: MessageService,
    public dialogService: DialogService,
    @Inject(DOCUMENT) private doc: Document,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private logger: NGXLogger,
    private supportService:SupportService,
    private userService: UserService,
    private firebaseStorageService: FirebaseStorageService
  ) {}

  // Chiudi il menu quando si clicca altrove nella pagina
  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // Mantieni il menu aperto solo se abbiamo cliccato sul bottone o sul menu stesso
    if (!target.closest('.config-dropdown') &&
        !target.closest('a[class*="align-items-center"]') &&
        !this.hideConfigMenu) {
      this.hideConfigMenu = true;
    }
    if (!target.closest('.config-dropdown') &&
        !target.closest('a[class*="align-items-center"]') &&
        !this.hideHelpMenu) {
      this.hideHelpMenu = true;
    }
  }

  ngOnInit() {
    // this.logger.info("[NAVBAR] INIT");

    // Controlla se siamo in ambiente di test o dev
    this.isTestEnvironment = environment.name === 'TEST' ;

    // Inizializza la rotta attiva
    this.updateActiveRoute();

    // Sottoscrizione ai cambiamenti di rotta
    this.subscription.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.updateActiveRoute();
        })
    );

    // Sottoscrizione allo stato di login
    this.subscription.add(
      this.userService.isLoggedIn$.subscribe(isLoggedIn => {
        this.isLoggedIn = isLoggedIn;
        // this.logger.info("[NAVBAR] Stato di login cambiato:", isLoggedIn);
      })
    );

    // Sottoscrizione alle informazioni dell'utente
    this.subscription.add(
      this.userService.userInfo$.subscribe(userInfo => {
        // this.logger.info("[NAVBAR] Dati utente aggiornati:", userInfo);

        const ruolo = userInfo.ruolo;
        // Reset dei ruoli
        this.isAdmin = false;
        this.isSuperAdmin = false;
        this.isUtente = false;
        this.isEnteEsecutore = false;

        // Imposta ruolo e chip in base alle informazioni ricevute
        if (ruolo === 'ADMIN') {
          this.isAdmin = true;
          this.navbarChip = "ADMIN";
          this.chipColor = '#B2675E';
          this.hideChip = false;
        } else if (ruolo === 'UMVS') {
          this.navbarChip = "UMVS";
          this.chipColor = '#5158BB';
          this.hideChip = false;
        } else if (ruolo === 'SUPERADMIN') {
          this.isSuperAdmin = true;
          this.navbarChip = "SUPERADMIN";
          this.chipColor = '#17B890';
          this.hideChip = false;
        } else if (ruolo === 'ENTE_ESECUTORE') {
          this.isUtente = true;
          this.isEnteEsecutore = true;
          this.navbarChip = "ENTE ESECUTORE";
          this.chipColor = '#02394A';
          this.hideChip = false;
        } else {
          this.hideChip = true;
        }
      })
    );
  }

  ngOnDestroy() {
    // Pulizia delle sottoscrizioni
    this.subscription.unsubscribe();
  }

  openConfigPanel(event?: MouseEvent){
    // Previeni la propagazione dell'evento per evitare che il listener document:click chiuda il menu
    if (event) {
      event.stopPropagation();
    }
    this.hideConfigMenu = !this.hideConfigMenu;
    // this.logger.info("[NAVBAR] Config menu visibility: " + (this.hideConfigMenu ? "hidden" : "visible"));
  }

  private updateActiveRoute() {
    const url = this.router.url;
    if (url.includes('/richieste')) {
      this.activeRoute = 'richieste';
    } else if (url.includes('/interventi')) {
      this.activeRoute = 'interventi';
    } else if (url.includes('/schede')) {
      this.activeRoute = 'schede';
    } else if (url.includes('/configurazione')) {
      this.activeRoute = 'configurazione';
    } else {
      this.activeRoute = '';
    }
  }

  isActiveRoute(route: string): boolean {
    return this.activeRoute === route;
  }

  openHelpPanel(event?: MouseEvent){
    // Previeni la propagazione dell'evento per evitare che il listener document:click chiuda il menu
    if (event) {
      event.stopPropagation();
    }
    this.hideHelpMenu = !this.hideHelpMenu;
  }

  goToRichieste(){
    // Naviga semplicemente a richieste, i filtri saranno ripristinati automaticamente dal componente
    this.router.navigate(['richieste']);
    this.closeMenu();
  }
  goToSchede(){
    this.router.navigateByUrl('/', {skipLocationChange: true}).then(()=>
    this.router.navigate(['schede']));
    this.closeMenu();
  }

  goToInterventi(){
    this.router.navigateByUrl('/', {skipLocationChange: true}).then(()=>
      this.router.navigate(['interventi']));
      this.closeMenu();
  }

  goToHome() {
    // Usa navigateByUrl con URL di base per pulire qualsiasi parametro query
    this.router.navigateByUrl('/');
    // this.logger.info("[NAVBAR] Navigazione alla home con URL pulito");
    this.closeMenu();
  }

  loginWithRedirect() {
    //// this.logger.info("** loginWithRedirect **");
    this.auth.loginWithRedirect().subscribe();
  }
  closeMenu() {
    this.hideMenu = true;
    this.hideComuniEUMenu = true;
    this.hideConfigMenu = true;
    this.hideHelpMenu = true;
  }

  openMenu(){
    this.hideMenu = false;
  }

  goToUtenti(){
    this.router.navigateByUrl('/', {skipLocationChange: true}).then(()=>
    this.router.navigate(['configurazione/utenti']));
    this.closeMenu();
  }

  goToFaq(){
    this.router.navigateByUrl('/', {skipLocationChange: true}).then(()=>
    this.router.navigate(['configurazione/faq']));
    this.closeMenu();
  }

  openProfileModule() {}


  logout() {
      localStorage.setItem("auth_token", "");
      localStorage.setItem("idUtente", "");
      localStorage.setItem("utente", "");
      localStorage.setItem("ruolo", "");
      localStorage.setItem("isLoggedIn", "false");

      // Aggiorna i subject nel UserService
      this.userService.clearUserData();

    this.auth.logout({ logoutParams: { returnTo: document.location.origin } });
  }

  toggleSubMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showSubMenu = !this.showSubMenu;
  }

  scaricaManuale() {
    const fileUrl = 'configurazioni/PMO_manuale.pdf';
    this.isDownloading = true;
    this.messageService.add({
      severity: 'info',
      summary: 'Download avviato',
      detail: 'Attendere la fine del download...',
      life: 1000
    });

    this.firebaseStorageService.downloadFile(fileUrl).subscribe({
      next: (blob: Blob) => {
        try {
          const nomeFile = 'manuale_PMO.pdf';
          saveAs(blob, nomeFile);
          this.isDownloading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Download completato',
            detail: 'Il manuale è stato scaricato con successo',
            life: 3000
          });
        } catch (error) {
          this.logger.error('[NAVBAR] Errore durante il salvataggio del file:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile salvare il file'
          });
          this.isDownloading = false;
        }
      },
      error: (error: Error) => {
        this.logger.error('[NAVBAR] Errore durante il download del manuale:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile scaricare il manuale'
        });
        this.isDownloading = false;
      }
    });
  }
  scaricaSinotticoSchede() {
    const fileUrl = 'configurazioni/quadro-sinottico.pdf';
    this.isDownloading = true;
    this.messageService.add({
      severity: 'info',
      summary: 'Download avviato',
      detail: 'Attendere la fine del download...',
      life: 1000
    });

    this.firebaseStorageService.downloadFile(fileUrl).subscribe({
      next: (blob: Blob) => {
        try {
          const nomeFile = 'quadro-sinottico.pdf';
          saveAs(blob, nomeFile);
          this.isDownloading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Download completato',
            detail: 'Il manuale è stato scaricato con successo',
            life: 3000
          });
        } catch (error) {
          this.logger.error('[NAVBAR] Errore durante il salvataggio del file:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile salvare il file'
          });
          this.isDownloading = false;
        }
      },
      error: (error: Error) => {
        this.logger.error('[NAVBAR] Errore durante il download del manuale:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile scaricare il manuale'
        });
        this.isDownloading = false;
      }
    });
  }

  openSupportModal() {
    if (this.supportModal) {
      this.supportModal.show();
    }
    this.closeMenu();
  }

  scaricaProgettoQGIS() {
    // Implementazione del metodo per scaricare il progetto QGIS
    this.logger.info("[NAVBAR] Download progetto QGIS richiesto");

    const fileUrl = 'https://pmo.map-hosting.it/sites/default/files/pmo_umvs.qgs';
    const fileName = 'pmo_umvs.qgs';

    // Crea un elemento anchor per il download
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';

    // Aggiungi l'elemento al DOM, simula il click e rimuovilo
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

   /*  this.messageService.add({
      severity: 'info',
      summary: 'Download',
      detail: 'Download del progetto QGIS avviato'
    }); */

    this.closeMenu();
  }
}
